/**
 * i18n 入口 — advisor-web 仅支持英文 / 中文两种。
 * 落地在 localStorage 'advisor_locale'，缺省 en。
 */

import { createContext, useContext, useEffect, useState } from 'react';

const STORAGE_KEY = 'advisor_locale';

const dictionaries = {
  en: () => import('./en.js'),
  zh: () => import('./zh.js')
};

const I18nContext = createContext({
  locale: 'en',
  setLocale: () => {},
  t: (key, vars) => key,
  raw: (key) => key,
  ready: false
});

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      if (v && dictionaries[v]) return v;
    } catch {
      /* ignore */
    }
    return 'en';
  });
  const [dict, setDict] = useState(null);

  useEffect(() => {
    let alive = true;
    dictionaries[locale]().then((mod) => {
      if (alive) setDict(mod.default || mod);
    });
    return () => {
      alive = false;
    };
  }, [locale]);

  const setLocale = (next) => {
    if (!dictionaries[next]) return;
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  };

  function t(key, vars) {
    if (!dict) return key;
    const parts = key.split('.');
    let cur = dict;
    for (const p of parts) {
      if (cur && typeof cur === 'object' && p in cur) cur = cur[p];
      else return key;
    }
    if (typeof cur !== 'string') return key;
    if (!vars) return cur;
    return cur.replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? String(vars[k]) : `{${k}}`));
  }

  // raw(key): 取结构化数据（对象/数组）。t() 只返回 string，取对象必须用 raw。
  function raw(key) {
    if (!dict) return null;
    const parts = key.split('.');
    let cur = dict;
    for (const p of parts) {
      if (cur && typeof cur === 'object' && p in cur) cur = cur[p];
      else return null;
    }
    return cur;
  }

  // dict 未加载完不渲染 children —— 否则 raw('home') 返回 null，页面 .map 崩溃白屏
  if (!dict) {
    return (
      <I18nContext.Provider value={{ locale, setLocale, t, raw, ready: false }}>
        <div style={{ minHeight: '100vh' }} aria-busy="true" />
      </I18nContext.Provider>
    );
  }

  return (
    <I18nContext.Provider value={{ locale, setLocale, t, raw, ready: !!dict }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}
